import { Buffer } from "buffer";
import { IconCollection } from "components/Icon/IconCollection";
import { ResourceConfig } from "config/config";
import {
    deleteActionSignature,
    DeleteListAction,
    deletePermanentlyActionSignature,
    downloadActionSignature,
    duplicateActionSignature,
    exportActionSignature,
    moveToLocationActionSignature,
    PermanentlyDeleteListAction,
} from "containers/Resources/components/CommonActions/CommonActions";
import {
    commentsTab,
    logsTab,
    logsTabKey,
    metadataTab,
    referencesUsedByTab,
} from "containers/Resources/screens/ResourcesSingle/constants";
import { formatFileSizeExact } from "components/Form/components/FileMeta/fileMeta";
import { buildSimpleColumn } from "containers/Search/components/SearchTabs/columns";
import resource from "./resource";

const resourceName = "file";

const config: ResourceConfig = {
    label: "File",
    icon: IconCollection.fileOutline,
    resourceName,
    resourceClass: "resource",
    defaultActiveTab: logsTabKey,
    layout: [
        { partial: "pageHeaderForm" },
        { partial: "form" },
        {
            partial: "tabs",
            config: {
                tabs: [logsTab.tab, commentsTab.tab, referencesUsedByTab.tab, metadataTab.tab],
                content: [logsTab.content, commentsTab.content, referencesUsedByTab.content, metadataTab.content],
            },
        },
    ],
    graphql: {
        resource: resourceName,
        resourceBody: `
            ${resource.graphql.resourceBody}
            content_size_bytes
            content_type
            content_encoding
        `,
        resourceBodyListing: `
            ${resource.graphql.resourceBody}
            content_size_bytes
            content_type
            content_encoding
        `,
        createVariables: `
            ${resource.graphql.createVariables(resourceName)}
            $content: String
            $is_bundle_content: Boolean
        `,
        createQuery: `
            ${resource.graphql.createQuery(resourceName)}
            content: $content
            is_bundle_content: $is_bundle_content
        `,
        updateVariables: `
            ${resource.graphql.updateVariables(resourceName)}
            $content: String,
        `,
        updateQuery: `
            ${resource.graphql.updateQuery(resourceName)}
            content: $content,
        `,
    },
    actions: {
        shown: [downloadActionSignature],
        hidden: [
            duplicateActionSignature,
            exportActionSignature,
            moveToLocationActionSignature,
            deleteActionSignature,
            deletePermanentlyActionSignature,
        ],
    },
    listActions: {
        shown: [],
        hidden: [
            { key: "delete", action: DeleteListAction },
            { key: "deletePermanently", action: PermanentlyDeleteListAction },
        ],
    },
    additionalColumns: (setNewValue) => [
        {
            label: "Content size in bytes",
            key: "content_size_bytes",
            shown: true,
            sortable: true,
            dto: (value: any) => {
                const content = <div>{value.content_size_bytes}</div>;
                return buildSimpleColumn(
                    content,
                    setNewValue
                        ? () =>
                              setNewValue({
                                  field: "content_size_bytes",
                                  value: value.content_size_bytes,
                              })
                        : undefined
                );
            },
        },
        {
            // The server-detected MIME content type is already fetched in this
            // resource's listing selection (resourceBodyListing above), so it can
            // be offered as a selectable result column. shown:false => available in
            // the column picker without changing the default file-list layout.
            // (operator-task-fe-advanced-search-file-content-type-column — interim
            // per-type fix ahead of the generic schema-driven column work.)
            label: "Content type",
            key: "content_type",
            shown: false,
            sortable: true,
            dto: (value: any) => {
                const content = <div>{value.content_type}</div>;
                return buildSimpleColumn(
                    content,
                    setNewValue
                        ? () =>
                              setNewValue({
                                  field: "content_type",
                                  value: value.content_type,
                              })
                        : undefined
                );
            },
        },
    ],
    form: [
        {
            name: "content_size_bytes",
            component: "fileSize",
            fieldProps: {
                label: "Content size",
            },
            fieldAttr: {
                // exact byte count on hover (see FileSizeFormComponent / Input tooltip)
                fieldTooltip: (values: any) => formatFileSizeExact(values?.content_size_bytes),
            },
        },
        {
            name: "content_type",
            component: "contentType",
            fieldProps: {
                label: "Content type",
            },
        },
        {
            name: "content_encoding",
            component: "contentEncoding",
            fieldProps: {
                // Server-detected text encoding, read-only. Stored bytes are never
                // re-encoded (files stay binary-identical); this is only used to
                // decode text for display. Empty for binary/undetectable content.
                // (operator-task-file-content-encoding-detection)
                label: "Content encoding",
            },
        },
        {
            name: "content",
            component: "switchContent",
            fieldProps: {
                label: "Content",
                lang: "text",
                isReadOnly: false,
                hiddenContentTooltip:
                    "Could not display large content. To see the content, please download the file.",
            },
            submitDto: (value: any) => Buffer.from(value, "binary").toString("base64"),
        },
    ],
    formDTO: {
        initialValueDTO: (data: any) => {
            const content =
                data?.content !== undefined
                    ? Buffer.from(data?.content, "base64").toString("binary")
                    : undefined;
            return { ...data, content };
        },
    },
};
export default config;
